import { MessagesSquare } from 'lucide-react';
import { memo } from 'react';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import { cn } from '@/lib/utils';
import { ChatSyncIndicator } from '../ChatSyncIndicator';
import type { ChatLayout, StatusBarInfo } from './types';

interface StatusBarProperties extends StatusBarInfo {
  className?: string;
  layout: ChatLayout;
  showSyncIndicator?: boolean;
}

export const StatusBar = memo(function StatusBarComponent({
  layout,
  className,
  showSyncIndicator = false,
  isLoading,
  isSyncing,
  streamingState,
  queuedMessages,
  recentEvent,
}: StatusBarProperties) {
  const isSidebar = layout === 'sidebar';
  const queuedCount = queuedMessages.length;
  const hasQueued = queuedCount > 0;
  let statusLabel = 'Idle';
  if (streamingState.started) {
    statusLabel = 'Streaming';
  } else if (isLoading) {
    statusLabel = 'Submitting';
  }
  const activityDetail = (() => {
    if (!recentEvent) {
      return null;
    }
    switch (recentEvent.type) {
      case 'tool-call': {
        return `Running ${recentEvent.toolName}${recentEvent.summary ? ` · ${recentEvent.summary}` : ''}`;
      }
      case 'tool-result': {
        return `${recentEvent.outcome === 'success' ? 'Finished' : 'Failed'} ${recentEvent.toolName}${
          recentEvent.summary ? ` · ${recentEvent.summary}` : ''
        }`;
      }
      case 'text-delta': {
        return recentEvent.deltaLength > 0 ? 'Responding…' : null;
      }
      default: {
        return null;
      }
    }
  })();
  if (isSidebar) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-t border-border/40 px-4 py-2 text-xs text-muted-foreground sm:px-6',
        'bg-card/70 backdrop-blur',
        className
      )}
    >
      <div className="flex items-center gap-2 font-medium text-card-foreground">
        {isSyncing || isLoading ? <CuteSpinner size={14} className="border-current/30 border-t-current" /> : null}
        <span>{activityDetail ?? statusLabel}</span>
      </div>
      <div className="flex items-center gap-3">
        {hasQueued ? (
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
            <MessagesSquare className="h-3 w-3" />
            <span>{queuedCount} queued</span>
          </div>
        ) : null}
        {showSyncIndicator ? <ChatSyncIndicator className="h-4" /> : null}
      </div>
    </div>
  );
});
